import { useTranslation } from 'react-i18next';

import { toastManager } from '@crm/entities/toast';
import { getApiErrorMessage } from '@crm/shared/api';

import { useChangeDealColumnMutation } from '../../api/useChangeDealColumnMutation';
import { useSortColumnMutation } from '../../api/useSortColumnMutation';

import type { TColumnDragEndResult, TDealDragEndResult } from './types';
import type { DragEndEvent } from '@dnd-kit/react';

type TUseChangeDealColumnParams = {
    handleDragEnd: (event: DragEndEvent) => void;
    getDealDragEndResult: (event: DragEndEvent) => TDealDragEndResult | undefined;
    getColumnDragEndResult: (event: DragEndEvent) => TColumnDragEndResult | undefined;
};

export const useDealsBoardDragEnd = ({
    handleDragEnd,
    getDealDragEndResult,
    getColumnDragEndResult,
}: TUseChangeDealColumnParams) => {
    const { t } = useTranslation();
    const changeDealColumnMutation = useChangeDealColumnMutation();
    const sortColumnMutation = useSortColumnMutation();

    const handleDndDragEnd = (event: DragEndEvent) => {
        handleDragEnd(event);

        const dealDragEndResult = getDealDragEndResult(event);

        if (dealDragEndResult) {
            changeDealColumnMutation.mutate(dealDragEndResult.payload, {
                onError: (err) => {
                    dealDragEndResult.rollback();
                    toastManager.onError({
                        title: getApiErrorMessage(
                            err,
                            t('crm.deals.board.dnd.changeDealError')
                        ),
                    });
                },
            });
            return;
        }

        const columnDragEndResult = getColumnDragEndResult(event);

        if (!columnDragEndResult) {
            return;
        }

        sortColumnMutation.mutate(columnDragEndResult.payload, {
            onError: (err) => {
                columnDragEndResult.rollback();
                toastManager.onError({
                    title: getApiErrorMessage(
                        err,
                        t('crm.deals.board.dnd.changeColumnError')
                    ),
                });
            },
        });
    };

    return {
        changeDealColumnMutation,
        handleDndDragEnd,
        sortColumnMutation,
    };
};
